import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { SignatureModal } from "@/components/shared/SignatureModal";
import { ShieldCheck, FileSignature, CheckCircle2 } from "lucide-react";

export default function PublicDocumentSignPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const documentId = id as Id<"documents">;

  const document = useQuery(
    api.documents.getPublicDocumentForSigning,
    documentId && token ? { documentId, token } : "skip"
  );

  const submitSignature = useMutation(api.documents.submitSignature);

  const [isSigning, setIsSigning] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  if (!id || !token) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 text-gray-500">
        Invalid or missing signing link.
      </div>
    );
  }

  if (document === undefined) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (document === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 text-red-500">
        Document not found or token is invalid.
      </div>
    );
  }

  const clientSigner = document.signers?.find((s: any) => s.role === "client");
  const isAlreadySigned = clientSigner?.status === "signed";

  if (hasSigned || isAlreadySigned) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <CheckCircle2 className="mx-auto mb-4 h-16 w-16 text-green-500" />
            <CardTitle className="text-2xl">Thank You</CardTitle>
            <CardDescription>
              The document has been successfully signed. A finalized copy will be emailed to you
              shortly.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleSaveSignature = async (dataUrl: string, title?: string) => {
    try {
      await submitSignature({
        documentId,
        role: "client",
        signatureDataUrl: dataUrl,
        token: token,
        title: title,
      });
      setHasSigned(true);
      setIsSigning(false);
    } catch (error) {
      console.error("Failed to sign:", error);
      alert("An error occurred while saving your signature. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Review and Sign Document
          </h2>
          <p className="mt-4 text-lg text-gray-500">
            Please review the following document and provide your signature at the bottom.
          </p>
        </div>

        {/* Document Viewer */}
        <Card className="overflow-hidden border-0 shadow-lg">
          <CardHeader className="sticky top-0 z-10 flex flex-row items-center justify-between border-b bg-white">
            <div>
              <CardTitle>{document.title}</CardTitle>
              <CardDescription>Prepared by ManageClean Pro</CardDescription>
            </div>
            <div className="flex items-center rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-600">
              <ShieldCheck className="mr-2 h-4 w-4" />
              Secure Link
            </div>
          </CardHeader>
          <CardContent className="bg-white p-0">
            {/* Display compiled PDF or HTML content */}
            {document.compiledPdfStorageId || document.signedPdfStorageId ? (
              <div className="flex flex-col gap-2 h-[70vh] w-full">
                <div className="flex justify-end">
                  <a 
                    href={`${(import.meta.env.VITE_CONVEX_URL || "https://hardy-lemur-357.convex.cloud").replace('.cloud', '.site')}/getDocument?id=${document.signedPdfStorageId || document.compiledPdfStorageId}&title=${encodeURIComponent(document.title || "Document")}`}
                    target="_blank" 
                    rel="noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Not rendering? Open PDF in a new tab
                  </a>
                </div>
                <iframe
                  src={`${(import.meta.env.VITE_CONVEX_URL || "https://hardy-lemur-357.convex.cloud").replace('.cloud', '.site')}/getDocument?id=${document.signedPdfStorageId || document.compiledPdfStorageId}&title=${encodeURIComponent(document.title || "Document")}#view=FitH`}
                  className="flex-1 w-full border-0"
                  title="Document Viewer"
                />
              </div>
            ) : (
              <div
                className="prose prose-sm sm:prose lg:prose-lg max-w-none p-8"
                dangerouslySetInnerHTML={{ __html: document.content || "No content available." }}
              />
            )}

            {/* Render Signatures if they exist AND the document is not fully signed/merged yet */}
            {document.signers && document.signers.length > 0 && !document.signedPdfStorageId && (
              <div className="mt-12 px-8 pb-12">
                <div 
                  className="mb-12 text-sm text-gray-700 leading-relaxed text-justify"
                  dangerouslySetInnerHTML={{ 
                    __html: document.customSignatureParagraph || "<em>IN WITNESS WHEREOF</em>, the parties hereto have executed this Janitorial Service Agreement as of the date first written below. Each individual signing below represents and warrants that they are duly authorized to execute and deliver this Agreement on behalf of their respective party." 
                  }}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  {/* Provider Column */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-6 pb-2 border-b">ManageClean Pro</h4>
                    <div className="space-y-8">
                      {document.signers.filter((s: any) => s.role === "provider").map((signer: any, idx: number) => (
                        <div key={idx} className="w-full max-w-sm">
                          {signer.status === "signed" && signer.signatureDataUrl ? (
                            <>
                              <img
                                src={signer.signatureDataUrl}
                                alt={`${signer.name} signature`}
                                className="mb-2 max-h-24 object-contain"
                              />
                              <div className="border-t border-gray-400 pt-2">
                                <p className="font-semibold text-gray-900">{signer.name}</p>
                                <p className="text-sm text-gray-500">Service Provider</p>
                                <p className="mt-1 text-xs text-gray-400">
                                  Signed: {new Date(signer.signedAt).toLocaleString()}
                                </p>
                              </div>
                            </>
                          ) : (
                            <div className="border-t border-gray-400 pt-2 mt-24">
                              <p className="font-semibold text-gray-900">{signer.name || "Provider Representative"}</p>
                              <p className="text-sm text-gray-500">Service Provider</p>
                              <p className="mt-1 text-xs text-gray-400 italic">Pending Signature</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Client Column */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-6 pb-2 border-b">Client</h4>
                    <div className="space-y-8">
                      {document.signers.filter((s: any) => s.role === "client").map((signer: any, idx: number) => (
                        <div key={idx} className="w-full max-w-sm">
                          {signer.status === "signed" && signer.signatureDataUrl ? (
                            <>
                              <img
                                src={signer.signatureDataUrl}
                                alt={`${signer.name} signature`}
                                className="mb-2 max-h-24 object-contain"
                              />
                              <div className="border-t border-gray-400 pt-2">
                                <p className="font-semibold text-gray-900">{signer.name}</p>
                                <p className="text-sm text-gray-500">Client Representative</p>
                                <p className="mt-1 text-xs text-gray-400">
                                  Signed: {new Date(signer.signedAt).toLocaleString()}
                                </p>
                              </div>
                            </>
                          ) : (
                            <div className="border-t border-gray-400 pt-2 mt-24">
                              <p className="font-semibold text-gray-900">{signer.name || "Client Representative"}</p>
                              <p className="text-sm text-gray-500">Client Representative</p>
                              <p className="mt-1 text-xs text-gray-400 italic">Pending Signature</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Area */}
        <div className="flex flex-col items-center rounded-lg border bg-white p-6 text-center shadow-sm">
          <h3 className="mb-2 text-lg font-medium text-gray-900">Ready to proceed?</h3>
          <p className="mb-6 max-w-lg text-sm text-gray-500">
            By signing this document, you agree to the terms and conditions outlined above. This
            electronic signature is legally binding.
          </p>
          <Button
            size="lg"
            onClick={() => setIsSigning(true)}
            className="w-full gap-2 px-8 sm:w-auto"
          >
            <FileSignature className="h-5 w-5" />
            Sign Document
          </Button>
        </div>
      </div>

      <SignatureModal
        isOpen={isSigning}
        onClose={() => setIsSigning(false)}
        onSave={handleSaveSignature}
        title={`Sign ${document.title}`}
      />
    </div>
  );
}
